import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import './DashboardSchool.css';
import Logo from '../../assets/Logo.svg';
import { supabase } from '../../lib/supabase';
import { evaluateSchoolSubscription } from '../../lib/subscription';
import { syncBranchesProvidersFromParent } from '../../lib/syncBranchProviders';
import DashboardSchoolOverview from '../../components/DashboardSchoolOverview/DashboardSchoolOverview';
import DashboardSchoolStudents from '../../components/DashboardSchoolStudents/DashboardSchoolStudents';
import DashboardSchoolTeacher from '../../components/DashboardSchoolTeacher/DashboardSchoolTeacher';
import DashboardSchoolAnalytics from '../../components/DashboardSchoolAnalytics/DashboardSchoolAnalytics';
import DashboardSchoolGrades from '../../components/DashboardSchoolGrades/DashboardSchoolGrades';
import DashboardSchoolSubjects from '../../components/DashboardSchoolSubjects/DashboardSchoolSubjects';
import DashboardSchoolCalendar from '../../components/DashboardSchoolCalendar/DashboardSchoolCalendar';
import DashboardSchoolBranches from '../../components/DashboardSchoolBranches/DashboardSchoolBranches';
import UserAccessModal from '../../components/UserAccessModal/UserAccessModal';

// Separate LoginForm component to prevent re-creation
const SchoolLoginForm = ({ loginForm, loginError, isLoading, handleLogin, handleInputChange, handleInputFocus, isPasswordVisible, isPasswordFocused, handlePasswordFocus, handlePasswordBlur, togglePasswordVisibility }) => (
  <div className="school-login-page">
    <div className="school-login-container">
      <div className="school-login-header">
        <img src={Logo} alt="My Digital School" className="school-login-logo" />
        <h1>My Digital Schools</h1>
        <p className="school-login-subtitle">School Dashboard Access</p>
      </div>

      <form className="school-login-form" onSubmit={handleLogin}>
        <div className="school-form-group">
          <label htmlFor="school_id">ID</label>
          <input
            type="text"
            id="school_id2"
            name="school_id"
            value={loginForm.school_id}
            onChange={handleInputChange}
            onFocus={handleInputFocus}
            placeholder="Enter your school ID"
            required
            className={loginError ? 'error' : ''}
            autoComplete="username"
            autoCapitalize="off"
            autoCorrect="off"
            spellCheck="false"
          />
        </div>

        <div className="school-form-group">
          <label htmlFor="password">Password</label>
          <div className="school-password-field-wrapper">
            <input
              type={isPasswordVisible ? 'text' : 'password'}
              id="password"
              name="password"
              value={loginForm.password}
              onChange={handleInputChange}
              onFocus={handlePasswordFocus}
              onBlur={handlePasswordBlur}
              placeholder="Enter your password"
              required
              className={loginError ? 'error' : ''}
              autoComplete="current-password"
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck="false"
            />
            {(isPasswordFocused || loginForm.password) && (
              <button
                type="button"
                className="school-password-toggle-btn"
                onClick={togglePasswordVisibility}
                onMouseDown={(e) => e.preventDefault()}
                aria-label={isPasswordVisible ? 'Hide password' : 'Show password'}
                tabIndex={-1}
              >
                {isPasswordVisible ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                    <line x1="1" y1="1" x2="23" y2="23"></line>
                  </svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                    <circle cx="12" cy="12" r="3"></circle>
                  </svg>
                )}
              </button>
            )}
          </div>
        </div>

        {loginError && (
          <div className="school-error-message">
            <span className="school-error-icon">⚠️</span>
            {loginError}
          </div>
        )}

        <button
          type="submit"
          className="school-login-button"
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <span className="school-loading-spinner"></span>
              Authenticating...
            </>
          ) : (
            'Sign In'
          )}
        </button>
      </form>

      <div className="school-login-footer">
        <p>School access required</p>
      </div>
    </div>
  </div>
);

const DashboardSchool = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Tab-scoped override keys (prevents branch tabs from overwriting main tab via shared localStorage)
  const SESSION_SCHOOL_ID_KEY = 'mds_active_school_id';
  const SESSION_SCHOOL_DATA_KEY = 'mds_active_school_data';

  const getActiveSchoolId = () => sessionStorage.getItem(SESSION_SCHOOL_ID_KEY) || localStorage.getItem('school_id');
  const getActiveSchoolDataRaw = () => sessionStorage.getItem(SESSION_SCHOOL_DATA_KEY) || localStorage.getItem('school_data');
  const isSessionOverrideActive = () => Boolean(sessionStorage.getItem(SESSION_SCHOOL_ID_KEY));

  const setActiveSchoolData = (school) => {
    try {
      const raw = JSON.stringify(school);
      if (isSessionOverrideActive()) {
        sessionStorage.setItem(SESSION_SCHOOL_DATA_KEY, raw);
      } else {
        localStorage.setItem('school_data', raw);
      }
    } catch (e) {
      }
  };

  // Authentication state
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loginForm, setLoginForm] = useState({
    school_id: '',
    password: ''
  });
  const [loginError, setLoginError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [isPasswordFocused, setIsPasswordFocused] = useState(false);

  const [activeTab, setActiveTab] = useState('Overview');

  // Real data state
  const [students, setStudents] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [grades, setGrades] = useState([]);
  const [gradeSections, setGradeSections] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [schoolData, setSchoolData] = useState(null);
  const [dataLoading, setDataLoading] = useState(true);
  const [refreshActivities, setRefreshActivities] = useState(0);
  const [showLogoutModal, setShowLogoutModal] = useState(false);

  // Super admin state (for user access management)
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [showUserAccessModal, setShowUserAccessModal] = useState(false);
  const [loggedInUser, setLoggedInUser] = useState(null); // For user-based login

  // Subscription state (for warnings)
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);
  const [showSubscriptionNotice, setShowSubscriptionNotice] = useState(false);

  // Read-only mode: school subscription is not accessible (expired/unpaid/not started).
  const isReadOnlyMode = Boolean(subscriptionInfo && subscriptionInfo.isAccessible === false);

  // Authentication functions
  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setLoginError('');

    try {
      const normalizedId = (loginForm.school_id || '').trim();

      // First, try school authentication (original flow)
      const { data: schoolData, error: schoolError } = await supabase
        .from('schools')
        .select('*')
        .ilike('school_id', normalizedId || '')
        .eq('password', loginForm.password)
        .single();

      if (schoolData && !schoolError) {
        // School login successful
        const sub = evaluateSchoolSubscription(schoolData);
        setSubscriptionInfo(sub);
        if (sub.status === 'expiring' || sub.status === 'expired' || sub.status === 'unpaid') {
          setShowSubscriptionNotice(true);
        } else {
          setShowSubscriptionNotice(false);
        }

        setIsAuthenticated(true);
        setIsSuperAdmin(true); // School admins are always super admins
        localStorage.setItem('school_dashboard_authenticated', 'true');
        localStorage.setItem('school_id', schoolData.id);
        localStorage.setItem('school_data', JSON.stringify(schoolData));
        localStorage.setItem('is_super_admin', 'true');
        localStorage.removeItem('logged_in_user');
        // Mirror into sessionStorage for this tab (safe; sessionStorage is tab-scoped)
        sessionStorage.setItem(SESSION_SCHOOL_ID_KEY, schoolData.id);
        sessionStorage.setItem(SESSION_SCHOOL_DATA_KEY, JSON.stringify(schoolData));
        return;
      }

      // If school login failed, try user authentication
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('*, schools!inner(*)')
        .ilike('user_id', normalizedId || '')
        .eq('password', loginForm.password)
        .eq('allow_dashboard_access', true)
        .single();

      if (userData && !userError) {
        // User login successful
        const userSchool = userData.schools;
        const sub = evaluateSchoolSubscription(userSchool);
        setSubscriptionInfo(sub);
        if (sub.status === 'expiring' || sub.status === 'expired' || sub.status === 'unpaid') {
          setShowSubscriptionNotice(true);
        } else {
          setShowSubscriptionNotice(false);
        }

        setIsAuthenticated(true);
        setIsSuperAdmin(userData.is_super_admin || false);
        setLoggedInUser(userData);
        localStorage.setItem('school_dashboard_authenticated', 'true');
        localStorage.setItem('school_id', userData.school_id);
        localStorage.setItem('school_data', JSON.stringify(userSchool));
        localStorage.setItem('is_super_admin', userData.is_super_admin ? 'true' : 'false');
        localStorage.setItem('logged_in_user', JSON.stringify(userData));
        // Mirror into sessionStorage for this tab (safe; sessionStorage is tab-scoped)
        sessionStorage.setItem(SESSION_SCHOOL_ID_KEY, userData.school_id);
        sessionStorage.setItem(SESSION_SCHOOL_DATA_KEY, JSON.stringify(userSchool));
        return;
      }

      // Both authentication methods failed
      setLoginError('Invalid ID or password');

    } catch (err) {
      setLoginError('Authentication failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setLoginForm({ school_id: '', password: '' });
    setLoginError('');
    setIsPasswordVisible(false);
    setIsPasswordFocused(false);
    setIsSuperAdmin(false);
    setLoggedInUser(null);
    localStorage.removeItem('school_dashboard_authenticated');
    localStorage.removeItem('school_id');
    localStorage.removeItem('school_data');
    localStorage.removeItem('is_super_admin');
    localStorage.removeItem('logged_in_user');
    sessionStorage.removeItem(SESSION_SCHOOL_ID_KEY);
    sessionStorage.removeItem(SESSION_SCHOOL_DATA_KEY);
    setShowLogoutModal(false);
    // Navigate to landing page
    navigate('/');
  };

  const handleLogoutClick = () => {
    setShowLogoutModal(true);
  };

  const handleCancelLogout = () => {
    setShowLogoutModal(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setLoginForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleInputFocus = () => {
    // Clear error when user starts interacting with inputs
    if (loginError) {
      setLoginError('');
    }
  };

  const togglePasswordVisibility = () => {
    setIsPasswordVisible(prev => !prev);
  };

  const handlePasswordFocus = (e) => {
    handleInputFocus(e);
    setIsPasswordFocused(true);
  };

  const handlePasswordBlur = () => {
    // Delay blur to allow button click
    setTimeout(() => {
      setIsPasswordFocused(false);
    }, 200);
  };

  // Check if user is already authenticated on component mount
  useEffect(() => {
    // If this tab was opened with ?schoolId=... (branch tab), store that in sessionStorage (tab-scoped)
    // so it does not affect other open tabs.
    const params = new URLSearchParams(location.search);
    const schoolIdParam = params.get('schoolId');
    if (schoolIdParam) {
      sessionStorage.setItem(SESSION_SCHOOL_ID_KEY, schoolIdParam);
      sessionStorage.removeItem(SESSION_SCHOOL_DATA_KEY); // force refresh for this tab
    } else {
      // If no param, clear override keys to keep normal main-dashboard behavior
      sessionStorage.removeItem(SESSION_SCHOOL_ID_KEY);
      sessionStorage.removeItem(SESSION_SCHOOL_DATA_KEY);
    }

    const isAuth = localStorage.getItem('school_dashboard_authenticated');
    if (isAuth === 'true') {
      setIsAuthenticated(true);
      // Restore super admin status
      const superAdmin = localStorage.getItem('is_super_admin');
      setIsSuperAdmin(superAdmin === 'true');
      // Restore logged in user data
      const storedUser = localStorage.getItem('logged_in_user');
      if (storedUser) {
        try {
          setLoggedInUser(JSON.parse(storedUser));
        } catch (e) {
          }
      }
    }
  }, [location.search]);

  // Fetch school-specific data from database
  const fetchSchoolData = React.useCallback(async () => {
    try {
      setDataLoading(true);
      const schoolId = getActiveSchoolId();
      const storedSchoolData = getActiveSchoolDataRaw();

      if (!schoolId) {return;
      }

      // Use stored school data if available, otherwise fetch from database
      if (storedSchoolData) {
        try {
          const parsedSchoolData = JSON.parse(storedSchoolData);
          setSchoolData(parsedSchoolData);
          const sub = evaluateSchoolSubscription(parsedSchoolData);
          setSubscriptionInfo(sub);
          // Show notice only if subscription is not accessible
          setShowSubscriptionNotice(sub.isAccessible === false);
        } catch (err) {
          }
      }

      // Fetch all school-related data + current school status
      const [studentsResult, teachersResult, gradesResult, subjectsResult, schoolResult] = await Promise.all([
        supabase.from('users').select('*').eq('school_id', schoolId).eq('role', 'student').order('created_at', { ascending: false }),
        supabase.from('users').select('*').eq('school_id', schoolId).eq('role', 'teacher').order('created_at', { ascending: false }),
        supabase.from('grades').select('*').eq('school_id', schoolId).order('order_index', { ascending: true }),
        supabase.from('subjects').select('*').eq('school_id', schoolId).order('name', { ascending: true }),
        supabase.from('schools').select('*').eq('id', schoolId).single()
      ]);

      // Update school data and subscription status from fresh database record
      if (schoolResult.data) {
        let schoolToUse = schoolResult.data;
        // If this is a branch and has no providers, inherit from parent so branches always match parent's providers
        if (schoolResult.data.parent_school_id) {
          const branchProviders = schoolResult.data.accessible_providers;
          const hasNoProviders = !branchProviders || (Array.isArray(branchProviders) && branchProviders.length === 0);
          if (hasNoProviders) {
            const { data: parentSchool } = await supabase
              .from('schools')
              .select('accessible_providers')
              .eq('id', schoolResult.data.parent_school_id)
              .single();
            const parentProviders = parentSchool?.accessible_providers || [];
            if (parentProviders.length > 0) {
              await supabase
                .from('schools')
                .update({ accessible_providers: parentProviders })
                .eq('id', schoolId);
              schoolToUse = { ...schoolResult.data, accessible_providers: parentProviders };
            }
          }
        }
        setSchoolData(schoolToUse);
        setActiveSchoolData(schoolToUse);
        const sub = evaluateSchoolSubscription(schoolToUse);
        setSubscriptionInfo(sub);
        // Show notice only if subscription is not accessible
        setShowSubscriptionNotice(sub.isAccessible === false);
        // If this is the parent school, sync its branches' providers (fixes pre-update branches)
        if (!schoolResult.data.parent_school_id) {
          syncBranchesProvidersFromParent(schoolId).catch(() => {});
        }
      }

      // Set other data
      setStudents(studentsResult.data || []);
      setTeachers(teachersResult.data || []);
      setGrades(gradesResult.data || []);
      setSubjects(subjectsResult.data || []);

      // Fetch grade sections if we have grades
      if (gradesResult.data && gradesResult.data.length > 0) {
        const gradeIds = gradesResult.data.map(g => g.id);
        const { data: sectionsData } = await supabase
          .from('grade_sections')
          .select('*')
          .in('grade_id', gradeIds)
          .order('order_index', { ascending: true });

        setGradeSections(sectionsData || []);
      } else {
        setGradeSections([]);
      }

    } catch (err) {
      } finally {
      setDataLoading(false);
    }
  }, []);

  const triggerActivityRefresh = () => {
    setRefreshActivities(prev => prev + 1);
  };

  // Fetch data when component mounts and user is authenticated
  useEffect(() => {
    if (isAuthenticated) {
      fetchSchoolData();
    }
  }, [isAuthenticated, fetchSchoolData]);

  // Realtime: if the school subscription changes while the dashboard is open, update banner + read-only instantly.
  useEffect(() => {
    if (!isAuthenticated) return undefined;

    const schoolDbId = getActiveSchoolId();
    if (!schoolDbId) return undefined;// Create a unique channel name to avoid conflicts
    const channel = supabase
      .channel(`school_realtime_${schoolDbId}_${Date.now()}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE', // Watch for updates
          schema: 'public',
          table: 'schools',
          filter: `id=eq.${schoolDbId}`,
        },
        async (payload) => {// IMPORTANT: Re-fetch full row to bypass partial payload issues
          const { data: freshSchool, error: fetchError } = await supabase
            .from('schools')
            .select('*')
            .eq('id', schoolDbId)
            .single();

          if (fetchError) {return;
          }

          if (freshSchool) {// Update all relevant states immediately
            setSchoolData(freshSchool);

            try {
              setActiveSchoolData(freshSchool);
            } catch (e) {
              }

            const sub = evaluateSchoolSubscription(freshSchool);setSubscriptionInfo(sub);

            // If inaccessible, force the danger notice to show
            if (sub.isAccessible === false) {setShowSubscriptionNotice(true);
            } else {
              // If it became accessible (subscription_paid = TRUE), hide the notice completelysetShowSubscriptionNotice(false);
            }
          }
        }
      );

    channel.subscribe((status) => {if (status === 'SUBSCRIBED') {} else if (status === 'CHANNEL_ERROR') {}
    });

    return () => {supabase.removeChannel(channel);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, location.search]);

  const tabs = [
    { id: 'Overview', label: 'Overview' },
    { id: 'Students', label: 'Students' },
    { id: 'Teachers', label: 'Teachers' },
    { id: 'Grades', label: 'Grades' },
    { id: 'Subjects', label: 'Subjects' },
    { id: 'Calendar', label: 'Calendar' },
    { id: 'Analytics', label: 'Analytics' },
    // Show Branches tab only if this is a Main Account (no parent) and has the feature enabled (optional check, for now show for all parents)
    ...(schoolData && !schoolData.parent_school_id ? [{ id: 'Branches', label: 'Branches' }] : [])
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'Overview':
        return (
          <DashboardSchoolOverview
            dataLoading={dataLoading}
            studentsCount={students.length}
            teachersCount={teachers.length}
            gradesCount={grades.length}
            subjectsCount={subjects.length}
            schoolData={schoolData}
            refreshActivities={refreshActivities}
          />
        );

      case 'Students':
        return (
          <DashboardSchoolStudents
            students={students}
            grades={grades}
            gradeSections={gradeSections}
            dataLoading={dataLoading}
            readOnly={isReadOnlyMode}
            onStudentAdded={() => { fetchSchoolData(); triggerActivityRefresh(); }}
          />
        );

      case 'Teachers':
        return (
          <DashboardSchoolTeacher
            teachers={teachers}
            subjects={subjects}
            grades={grades}
            gradeSections={gradeSections}
            dataLoading={dataLoading}
            readOnly={isReadOnlyMode}
            onTeacherAdded={() => { fetchSchoolData(); triggerActivityRefresh(); }}
          />
        );

      case 'Grades':
        return (
          <DashboardSchoolGrades
            grades={grades}
            gradeSections={gradeSections}
            dataLoading={dataLoading}
            readOnly={isReadOnlyMode}
            onRefresh={() => { fetchSchoolData(); triggerActivityRefresh(); }}
          />
        );

      case 'Subjects':
        return (
          <DashboardSchoolSubjects
            subjects={subjects}
            dataLoading={dataLoading}
            readOnly={isReadOnlyMode}
            onRefresh={() => { fetchSchoolData(); triggerActivityRefresh(); }}
          />
        );

      case 'Calendar':
        return (
          <DashboardSchoolCalendar
            schoolId={getActiveSchoolId()}
            grades={grades}
            gradeSections={gradeSections}
            teachers={teachers}
            readOnly={isReadOnlyMode}
          />
        );

      case 'Analytics':
        return (
          <DashboardSchoolAnalytics
            dataLoading={dataLoading}
            setDataLoading={setDataLoading}
            students={students}
            teachers={teachers}
            schoolData={schoolData}
          />
        );

      case 'Branches':
        return (
          <DashboardSchoolBranches
            schoolData={schoolData}
            dataLoading={dataLoading}
            onRefresh={() => { fetchSchoolData(); }}
          />
        );

      default:
        return null;
    }
  };

  // If not authenticated, show login form
  if (!isAuthenticated) {
    return (
      <SchoolLoginForm
        loginForm={loginForm}
        loginError={loginError}
        isLoading={isLoading}
        handleLogin={handleLogin}
        handleInputChange={handleInputChange}
        handleInputFocus={handleInputFocus}
        isPasswordVisible={isPasswordVisible}
        isPasswordFocused={isPasswordFocused}
        handlePasswordFocus={handlePasswordFocus}
        handlePasswordBlur={handlePasswordBlur}
        togglePasswordVisibility={togglePasswordVisibility}
      />
    );
  }

  return (
    <div className="school-dashboard-page">
      <header className="school-dashboard-header">
        <div className="school-dashboard-header-inner">
          <div className="school-header-left">
            <img src={Logo} alt="My Digital School" className="school-header-logo" />
            <h1>My Digital Schools</h1>
          </div>
          <div className="school-header-right">
            {/* User Access Management Button (Super Admin Only) */}
            {isSuperAdmin && (
              <button
                className="school-user-access-btn"
                onClick={() => setShowUserAccessModal(true)}
                title="Manage Dashboard Access"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M12 22C12 22 20 18 20 12V5L12 2L4 5V12C4 18 12 22 12 22Z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M9 12L11 14L15 10"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                Access
              </button>
            )}
            <button className="school-logout-btn" onClick={handleLogoutClick} title="Logout">
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="school-logout-icon"
              >
                <path
                  d="M9 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H9"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M16 17L21 12L16 7"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M21 12H9"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Logout
            </button>
          </div>
        </div>
      </header>

      <main className="school-dashboard-main">
        <div className="school-dashboard-title">
          <h2>{schoolData ? `${schoolData.name} Dashboard` : 'School Dashboard'}</h2>
          <p>Welcome back! Here's what's happening with your school.</p>
        </div>

        {subscriptionInfo && showSubscriptionNotice && (
          <div
            className={`subscription-notice ${subscriptionInfo.status === 'expiring' ? 'warning' : 'danger'}`}
          >
            {subscriptionInfo.status === 'expiring' && (
              <>
                <strong>Subscription Expiring Soon:</strong>{' '}
                {typeof subscriptionInfo.daysRemaining === 'number'
                  ? `Your subscription expires in ${subscriptionInfo.daysRemaining} day(s).`
                  : 'Your subscription is expiring soon.'}
              </>
            )}
            {subscriptionInfo.status === 'expired' && (
              <>
                <strong>Subscription Expired:</strong> Please contact the system administrator to renew.
              </>
            )}
            {subscriptionInfo.status === 'unpaid' && (
              <>
                <strong>Payment Required:</strong> Your school is currently marked as unpaid. Users will be blocked from logging in.
              </>
            )}
            {/* <button className="subscription-notice-close" onClick={() => setShowSubscriptionNotice(false)} aria-label="Close">
              ×
            </button> */}
          </div>
        )}

        <nav className="school-dashboard-tabs">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              className={`school-tab-button ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </nav>

        <div className="school-dashboard-content-wrapper">
          {renderTabContent()}
        </div>
      </main>

      {/* Logout Confirmation Modal */}
      {showLogoutModal && (
        <div className="school-logout-modal-overlay" onClick={handleCancelLogout}>
          <div className="school-logout-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="school-logout-modal-header">
              <div className="school-logout-modal-icon">
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="school-logout-icon"
                >
                  <path
                    d="M9 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H9"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M16 17L21 12L16 7"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M21 12H9"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <h3>Confirm Logout</h3>
            </div>

            <div className="school-logout-modal-body">
              <p>Are you sure you want to logout from the school dashboard?</p>
              <p className="school-logout-modal-subtitle">You will need to sign in again to access the school panel.</p>
            </div>

            <div className="school-logout-modal-actions">
              <button
                className="school-logout-modal-btn school-logout-cancel-btn"
                onClick={handleCancelLogout}
              >
                Cancel
              </button>
              <button
                className="school-logout-modal-btn school-logout-confirm-btn"
                onClick={handleLogout}
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      )}

      {/* User Access Modal */}
      <UserAccessModal
        isOpen={showUserAccessModal}
        onClose={() => setShowUserAccessModal(false)}
        schoolId={getActiveSchoolId()}
        schoolData={schoolData}
        onUserAdded={fetchSchoolData}
      />
    </div>
  );
};

export default DashboardSchool;
